/**
 * Copyright (c) 2024-2026 Ronan LE MEILLAT
 * License: AGPL-3.0-or-later
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

interface ImageLightboxProps {
  images: { url: string }[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Full-screen, keyboard/arrow-navigable image viewer. Rendered via a portal
 * to document.body so it escapes any sticky/transformed ancestor (the
 * product detail page's image column is `md:sticky`).
 */
export function ImageLightbox({ images, initialIndex, isOpen, onClose }: ImageLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (isOpen) setCurrentIndex(initialIndex);
  }, [isOpen, initialIndex]);

  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setCurrentIndex((i) => (i - 1 + images.length) % images.length);
      if (e.key === "ArrowRight") setCurrentIndex((i) => (i + 1) % images.length);
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, images.length, onClose]);

  if (!isOpen || images.length === 0) return null;

  const goPrev = () => setCurrentIndex((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setCurrentIndex((i) => (i + 1) % images.length);

  const content = (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute top-4 right-4 text-white/80 hover:text-white"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
      >
        <X className="w-8 h-8" />
      </button>

      {images.length > 1 && (
        <button
          type="button"
          aria-label="Previous image"
          className="absolute left-2 sm:left-6 text-white/80 hover:text-white"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
        >
          <ChevronLeft className="w-10 h-10" />
        </button>
      )}

      <div
        className="bg-white rounded-lg p-4 max-w-[90vw] max-h-[85vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[currentIndex].url}
          alt=""
          className="max-w-full max-h-[75vh] object-contain"
        />
      </div>

      {images.length > 1 && (
        <button
          type="button"
          aria-label="Next image"
          className="absolute right-2 sm:right-6 text-white/80 hover:text-white"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
        >
          <ChevronRight className="w-10 h-10" />
        </button>
      )}

      {images.length > 1 && (
        <span
          className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm"
          onClick={(e) => e.stopPropagation()}
        >
          {currentIndex + 1} / {images.length}
        </span>
      )}
    </div>
  );

  return typeof document !== "undefined" ? createPortal(content, document.body) : content;
}
